import type { SchemaInput, SchemaRecord } from './schema-input.js';
import { isBoolean, isNumber, isObject, isString } from './runtime-values.js';
import type { Point } from '../types/app.js';

export interface ServiceAreaBuilderDraft {
  points?: Point[];
  step?: number;
  fields?: {
    businessName?: string;
    showBusinessName?: boolean;
    title?: string;
    description?: string;
    placeholder?: string;
    ctaLabel?: string;
    ctaHref?: string;
    outsideCtaLabel?: string;
    outsideCtaHref?: string;
    enableGeolocation?: boolean;
    mapVisibility?: 'after-check' | 'always' | 'hidden';
    layout?: 'overlay' | 'split';
    insideMessage?: string;
    outsideMessage?: string;
    themeColor?: string;
  };
  testAddress?: string;
}

export interface PreparedServiceAreaDraft {
  draft: ServiceAreaBuilderDraft;
  mapState: { center: Point; zoom: number };
}

const STRING_FIELD_LIMITS = {
  businessName: 200,
  title: 200,
  description: 2_000,
  placeholder: 200,
  ctaLabel: 200,
  ctaHref: 2_000,
  outsideCtaLabel: 200,
  outsideCtaHref: 2_000,
  insideMessage: 2_000,
  outsideMessage: 2_000,
  themeColor: 64,
} as const;

function finitePoint(value: SchemaInput): value is Point {
  if (!value || !isObject(value) || Array.isArray(value)) return false;
  // SAFETY: The adjacent object and array checks establish a string-keyed record.
  const point = value as SchemaRecord;
  return isNumber(point.lat) && isNumber(point.lng)
    && Number.isFinite(point.lat) && Number.isFinite(point.lng)
    && point.lat >= -90 && point.lat <= 90
    && point.lng >= -180 && point.lng <= 180;
}

function validFields(value: SchemaInput): value is NonNullable<ServiceAreaBuilderDraft['fields']> {
  if (!value || !isObject(value) || Array.isArray(value)) return false;
  // SAFETY: The adjacent object and array checks establish a string-keyed record.
  const fields = value as SchemaRecord;
  for (const [name, limit] of Object.entries(STRING_FIELD_LIMITS)) {
    const field = fields[name];
    if (field !== undefined && (!isString(field) || field.length > limit)) return false;
  }
  for (const name of ['showBusinessName', 'enableGeolocation']) {
    const field = fields[name];
    if (field !== undefined && !isBoolean(field)) return false;
  }
  if (fields.mapVisibility !== undefined && !['after-check', 'always', 'hidden'].includes(String(fields.mapVisibility))) return false;
  if (fields.layout !== undefined && !['overlay', 'split'].includes(String(fields.layout))) return false;
  return true;
}

export function parseServiceAreaBuilderDraft(value: SchemaInput): ServiceAreaBuilderDraft | null {
  if (!value || !isObject(value) || Array.isArray(value)) return null;
  // SAFETY: The adjacent object and array checks establish a string-keyed record.
  const draft = value as SchemaRecord;
  if (draft.points !== undefined
    && (!Array.isArray(draft.points) || draft.points.length > 10_000 || !draft.points.every(finitePoint))) return null;
  if (draft.step !== undefined
    && (!isNumber(draft.step) || !Number.isInteger(draft.step) || draft.step < 0 || draft.step > 3)) return null;
  if (draft.fields !== undefined && !validFields(draft.fields)) return null;
  if (draft.testAddress !== undefined && (!isString(draft.testAddress) || draft.testAddress.length > 2_000)) return null;
  // SAFETY: Every supported draft field is validated above before this value is returned.
  return draft as ServiceAreaBuilderDraft;
}

export function parsePreparedServiceAreaDraft(value: SchemaInput): PreparedServiceAreaDraft | null {
  if (!value || !isObject(value) || Array.isArray(value)) return null;
  // SAFETY: The adjacent object and array checks establish a string-keyed record.
  const payload = value as SchemaRecord;
  const draft = parseServiceAreaBuilderDraft(payload.draft);
  if (!draft || !payload.mapState || !isObject(payload.mapState) || Array.isArray(payload.mapState)) return null;
  // SAFETY: The adjacent object and array checks establish a string-keyed map-state record.
  const mapState = payload.mapState as SchemaRecord;
  if (!finitePoint(mapState.center) || !isNumber(mapState.zoom)
    || !Number.isFinite(mapState.zoom) || mapState.zoom < 0 || mapState.zoom > 24) return null;
  return { draft, mapState: { center: mapState.center, zoom: mapState.zoom } };
}
